import { useEffect, useMemo, useState } from "react";
import {
  isOperationFinished,
  useAsyncOperation,
  type CrossSiteCallout,
  type CrossSiteCategoryCell,
  type CrossSiteHeadline,
  type CrossSitePerLineCell,
  type CrossSiteRecommendation,
  type CrossSiteResult,
} from "../api";
import { ProgressBar } from "./ProgressBar";
import { currencySymbol } from "../utils/currency";
import styles from "./ProcurementCompareModal.module.css";

// The cross-site comparison LRO writes its full CrossSiteResult onto
// the operation row's ``triage`` field. The FE polls /async_status,
// reads triage.cross_site_comparison when the op reaches "completed",
// and renders the structured analysis.

interface Props {
  operationKey: string;
  onClose: () => void;
}

interface TriageWithComparison {
  cross_site_comparison?: CrossSiteResult;
}

export const ProcurementCompareModal = ({ operationKey, onClose }: Props) => {
  const { data: op } = useAsyncOperation(operationKey, {
    enabled: Boolean(operationKey),
  });
  const [result, setResult] = useState<CrossSiteResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!op) return;
    if (op.status === "completed") {
      const triage = op.triage as TriageWithComparison | null;
      if (triage?.cross_site_comparison) {
        setResult(triage.cross_site_comparison);
      } else {
        setError(
          "The comparison completed but the result payload couldn't be parsed.",
        );
      }
    } else if (isOperationFinished(op.status)) {
      setError(
        op.error ??
          "The comparison failed without a specific error message.",
      );
    }
  }, [op]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          <h2 className={styles.title}>Cross-site procurement comparison</h2>
          <button
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className={styles.body}>
          {result ? (
            <CompareResultView result={result} />
          ) : error ? (
            <div className={styles.error}>{error}</div>
          ) : (
            <div className={styles.loading}>
              {op?.progress ? (
                <ProgressBar progress={op.progress} />
              ) : op?.status === "running" ? (
                "Reasoning over the selected sites' cost build-ups + surveys on Bedrock (typically 30-90 seconds)..."
              ) : (
                "Submitting the comparison..."
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const formatPounds = (
  n: number | null | undefined,
  symbol: string,
): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  return `${symbol}${Math.round(n).toLocaleString("en-GB")}`;
};

const formatPerKwp = (n: number | null | undefined, symbol: string): string =>
  formatPounds(n, symbol);

const formatPct = (n: number | null | undefined): string => {
  if (n === null || n === undefined || !Number.isFinite(n)) return "-";
  if (Math.abs(n) < 0.05) return "0.0%";
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
};

const CompareResultView = ({ result }: { result: CrossSiteResult }) => {
  const headlinesSorted = useMemo(
    () => [...result.headlines].sort((a, b) => a.rank - b.rank),
    [result.headlines],
  );
  // The comparison is single-currency by construction, so one symbol drives
  // every figure and section title.
  const symbol = currencySymbol(result.currency_code);
  return (
    <div className={styles.resultRoot}>
      <Section title="Headline">
        <p className={styles.headline}>{result.comparison.headline}</p>
        <HeadlineTable headlines={headlinesSorted} symbol={symbol} />
      </Section>

      {result.comparison.key_insights.length > 0 && (
        <Section title="Key insights">
          <ul className={styles.insightList}>
            {result.comparison.key_insights.map((insight, i) => (
              <li key={i} className={styles.insightItem}>
                {insight}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {result.comparison.recommendations.length > 0 && (
        <Section title="Recommendations">
          <ul className={styles.recList}>
            {result.comparison.recommendations.map((rec, i) => (
              <RecommendationCard key={i} rec={rec} />
            ))}
          </ul>
        </Section>
      )}

      <Section title={`${symbol}/kWp by category`}>
        <CategoryRollupTable
          headlines={headlinesSorted}
          cells={result.category_rollup}
          symbol={symbol}
        />
      </Section>

      <Section title={`${symbol}/kWp by line item (cheapest site = base)`}>
        <PerLineMatrixTable
          headlines={headlinesSorted}
          cells={result.per_line_matrix}
          symbol={symbol}
        />
      </Section>

      {result.comparison.per_site_callouts.length > 0 && (
        <Section title="Per-site callouts">
          <ul className={styles.calloutList}>
            {result.comparison.per_site_callouts.map((c, i) => (
              <PerSiteCallout
                key={i}
                callout={c}
                headlines={headlinesSorted}
              />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className={styles.section}>
    <h3 className={styles.sectionTitle}>{title}</h3>
    {children}
  </section>
);

const HeadlineTable = ({
  headlines,
  symbol,
}: {
  headlines: CrossSiteHeadline[];
  symbol: string;
}) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th className={styles.th}>Site</th>
        <th className={`${styles.th} ${styles.thNumeric}`}>Size (kWp)</th>
        <th className={`${styles.th} ${styles.thNumeric}`}>Total</th>
        <th className={`${styles.th} ${styles.thNumeric}`}>{symbol}/kWp</th>
        <th className={`${styles.th} ${styles.thNumeric}`}>Rank</th>
        <th className={`${styles.th} ${styles.thNumeric}`}>vs cheapest</th>
      </tr>
    </thead>
    <tbody>
      {headlines.map((h) => (
        <tr key={h.project_id}>
          <td className={styles.td}>{h.name}</td>
          <td className={`${styles.td} ${styles.tdNumeric}`}>
            {h.size_kwp !== null
              ? Math.round(h.size_kwp).toLocaleString("en-GB")
              : "-"}
          </td>
          <td className={`${styles.td} ${styles.tdNumeric}`}>
            {formatPounds(h.total_pounds, symbol)}
          </td>
          <td className={`${styles.td} ${styles.tdNumeric} ${styles.tdBold}`}>
            {formatPerKwp(h.adjusted_per_kwp, symbol)}
          </td>
          <td className={`${styles.td} ${styles.tdNumeric}`}>{h.rank}</td>
          <td className={`${styles.td} ${styles.tdNumeric}`}>
            {formatPct(h.pct_vs_cheapest)}
          </td>
        </tr>
      ))}
    </tbody>
  </table>
);

const RecommendationCard = ({
  rec,
}: {
  rec: CrossSiteRecommendation;
}) => (
  <li className={styles.recItem}>
    <div className={styles.recHeader}>
      <span
        className={`${styles.priority} ${
          rec.priority === "high"
            ? styles.priorityHigh
            : rec.priority === "medium"
              ? styles.priorityMedium
              : styles.priorityLow
        }`}
      >
        {rec.priority}
      </span>
      <span className={styles.recArea}>{rec.area}</span>
    </div>
    <p className={styles.recBody}>{rec.recommendation}</p>
    <p className={styles.recWhy}>
      <span className={styles.recWhyLabel}>Why:</span> {rec.why}
    </p>
  </li>
);

const CategoryRollupTable = ({
  headlines,
  cells,
  symbol,
}: {
  headlines: CrossSiteHeadline[];
  cells: CrossSiteCategoryCell[];
  symbol: string;
}) => {
  const categories = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const c of cells) {
      if (!seen.has(c.category)) {
        seen.add(c.category);
        order.push(c.category);
      }
    }
    return order;
  }, [cells]);
  const byKey = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const c of cells) {
      map.set(`${c.project_id}::${c.category}`, c.per_kwp);
    }
    return map;
  }, [cells]);
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}>Category</th>
          {headlines.map((h) => (
            <th
              key={h.project_id}
              className={`${styles.th} ${styles.thNumeric}`}
            >
              {h.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {categories.map((cat) => (
          <tr key={cat}>
            <td className={styles.td}>{cat}</td>
            {headlines.map((h) => (
              <td
                key={h.project_id}
                className={`${styles.td} ${styles.tdNumeric}`}
              >
                {formatPerKwp(
                  byKey.get(`${h.project_id}::${cat}`) ?? null,
                  symbol,
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const PerLineMatrixTable = ({
  headlines,
  cells,
  symbol,
}: {
  headlines: CrossSiteHeadline[];
  cells: CrossSitePerLineCell[];
  symbol: string;
}) => {
  const lineLabels = useMemo(() => {
    const seen = new Set<string>();
    const order: string[] = [];
    for (const c of cells) {
      if (!seen.has(c.line_label)) {
        seen.add(c.line_label);
        order.push(c.line_label);
      }
    }
    return order;
  }, [cells]);
  const byKey = useMemo(() => {
    const map = new Map<string, number | null>();
    for (const c of cells) {
      map.set(`${c.project_id}::${c.line_label}`, c.per_kwp);
    }
    return map;
  }, [cells]);
  // Cheapest site = rank 1. Use it as the base column for the ratios
  // (matches the Cowork "BRS2 base" UX, with the multiplier displayed
  // inline as "x1.166").
  const base = headlines[0] ?? null;
  return (
    <div className={styles.matrixWrap}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.th}>Line item</th>
            {headlines.map((h) => (
              <th
                key={h.project_id}
                className={`${styles.th} ${styles.thNumeric}`}
              >
                {h.name}
                {base !== null && h.project_id !== base.project_id && (
                  <span className={styles.thRatio}>vs {base.name}</span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lineLabels.map((label) => {
            const baseValue =
              base !== null
                ? byKey.get(`${base.project_id}::${label}`) ?? null
                : null;
            return (
              <tr key={label}>
                <td className={styles.td}>{label}</td>
                {headlines.map((h) => {
                  const v =
                    byKey.get(`${h.project_id}::${label}`) ?? null;
                  const ratio =
                    base !== null &&
                    h.project_id !== base.project_id &&
                    baseValue !== null &&
                    baseValue !== 0 &&
                    v !== null
                      ? v / baseValue
                      : null;
                  return (
                    <td
                      key={h.project_id}
                      className={`${styles.td} ${styles.tdNumeric}`}
                    >
                      {formatPerKwp(v, symbol)}
                      {ratio !== null && (
                        <span className={styles.cellRatio}>
                          x{ratio.toFixed(3)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PerSiteCallout = ({
  callout,
  headlines,
}: {
  callout: CrossSiteCallout;
  headlines: CrossSiteHeadline[];
}) => {
  const site = headlines.find((h) => h.project_id === callout.project_id);
  return (
    <li className={styles.calloutItem}>
      <span className={styles.calloutSite}>
        {site?.name ?? callout.project_id}
      </span>
      <span className={styles.calloutText}>{callout.callout}</span>
    </li>
  );
};
